import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const LocationManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentType, setCurrentType] = useState('country');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    dial_code: '',
    country_id: '',
    state_id: '',
    zip_code: '',
    is_active: 1
  });
  
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCountries();
    fetchStates();
    fetchCities();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await api.get('/location/countries');
      setCountries(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch countries');
    }
  };

  const fetchStates = async (countryId = null) => {
    try {
      const url = countryId ? `/location/states?country_id=${countryId}` : '/location/states';
      const response = await api.get(url);
      setStates(response.data.data);
      if (countryId) {
        setFilteredStates(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch states');
    }
  };

  const fetchCities = async (stateId = null) => {
    try {
      const url = stateId ? `/location/cities?state_id=${stateId}` : '/location/cities';
      const response = await api.get(url);
      setCities(response.data.data);
      if (stateId) {
        setFilteredCities(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch cities');
    }
  };

  const handleOpenDialog = (type, item = null) => {
    setCurrentType(type);
    setEditingItem(item);
    
    if (item) {
      setFormData({
        name: item.name || '',
        code: item.code || '',
        dial_code: item.dial_code || '',
        country_id: item.country_id || '',
        state_id: item.state_id || '',
        zip_code: item.zip_code || '',
        is_active: item.is_active === 1
      });
    } else {
      setFormData({
        name: '',
        code: '',
        dial_code: '',
        country_id: '',
        state_id: '',
        zip_code: '',
        is_active: 1
      });
    }
    setDialogOpen(true);
  };
const handleSave = async () => {
  if (!formData.name) {
    toast.error('Name is required');
    return;
  }

  setLoading(true);
  try {
    let response;
    // Fix the URL - use correct plural forms
    let url;
    if (currentType === 'country') {
      url = '/location/countries';
    } else if (currentType === 'state') {
      url = '/location/states';
    } else {
      url = '/location/cities';  // Changed from 'citys' to 'cities'
    }
    
    if (editingItem) {
      response = await api.put(`${url}/${editingItem.id}`, formData);
    } else {
      response = await api.post(url, formData);
    }
    
    if (response.data.success) {
      toast.success(`${currentType.charAt(0).toUpperCase() + currentType.slice(1)} ${editingItem ? 'updated' : 'added'} successfully`);
      setDialogOpen(false);
      
      // Refresh data based on type
      if (currentType === 'country') {
        fetchCountries();
      } else if (currentType === 'state') {
        fetchStates();
      } else {
        fetchCities();
      }
    }
  } catch (error) {
    console.error('Save error:', error);
    toast.error(error.response?.data?.message || `Failed to save ${currentType}`);
  } finally {
    setLoading(false);
  }
};
  const handleDelete = async (type, id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        const response = await api.delete(`/location/${type}s/${id}`);
        if (response.data.success) {
          toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
          
          if (type === 'country') {
            fetchCountries();
          } else if (type === 'state') {
            fetchStates();
          } else {
            fetchCities();
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to delete ${type}`);
      }
    }
  };

  const handleCountryChange = async (countryId) => {
    setFormData({ ...formData, country_id: countryId, state_id: '' });
    if (countryId) {
      await fetchStates(countryId);
    } else {
    }
  };

  const handleStateChange = async (stateId) => {
    setFormData({ ...formData, state_id: stateId });
    if (stateId) {
      await fetchCities(stateId);
    } else {
      setFilteredCities([]);
    }
  };

  const getCountryName = (countryId) => {
    const country = countries.find(c => c.id == countryId);
    return country ? country.name : 'N/A';
  };

  const getStateName = (stateId) => {
    const state = states.find(s => s.id == stateId);
    return state ? state.name : 'N/A';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Location Management
      </Typography>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Countries" />
          <Tab label="States" />
          <Tab label="Cities" />
        </Tabs>

        {/* Countries Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('country')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add Country
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Dial Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {countries.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((country) => (
                  <TableRow key={country.id} hover>
                    <TableCell>{country.code}</TableCell>
                    <TableCell>{country.name}</TableCell>
                    <TableCell>{country.dial_code || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={country.is_active ? 'Active' : 'Inactive'}
                        color={country.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenDialog('country', country)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete('country', country.id, country.name)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* States Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search states..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('state')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add State
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Country</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {states.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase())).map((state) => (
                  <TableRow key={state.id} hover>
                    <TableCell>{getCountryName(state.country_id)}</TableCell>
                    <TableCell>{state.code || '-'}</TableCell>
                    <TableCell>{state.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={state.is_active ? 'Active' : 'Inactive'}
                        color={state.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenDialog('state', state)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete('state', state.id, state.name)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Cities Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search cities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('city')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add City
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Country</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>City Name</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cities.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map((city) => (
                  <TableRow key={city.id} hover>
                    <TableCell>{city.country_name || getCountryName(city.country_id)}</TableCell>
                    <TableCell>{city.state_name || getStateName(city.state_id)}</TableCell>
                    <TableCell>{city.name}</TableCell>
                    <TableCell>{city.zip_code || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={city.is_active ? 'Active' : 'Inactive'}
                        color={city.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenDialog('city', city)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete('city', city.id, city.name)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
          color: '#fff'
        }}>
          {editingItem ? 'Edit' : 'Add'} {currentType.charAt(0).toUpperCase() + currentType.slice(1)}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {currentType === 'country' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Country Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="US, NP, IN"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Dial Code"
                    value={formData.dial_code}
                    onChange={(e) => setFormData({ ...formData, dial_code: e.target.value })}
                    placeholder="+1, +977, +91"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Country Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
              </>
            )}

            {currentType === 'state' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.country_id}
                      label="Country"
                      onChange={(e) => handleCountryChange(e.target.value)}
                    >
                      {countries.map(country => (
                        <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="State Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="State Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
              </>
            )}

            {currentType === 'city' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.country_id}
                      label="Country"
                      onChange={(e) => handleCountryChange(e.target.value)}
                    >
                      {countries.map(country => (
                        <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={formData.state_id}
                      label="State"
                      onChange={(e) => handleStateChange(e.target.value)}
                      disabled={!formData.country_id}
                    >
                      {filteredStates.map(state => (
                        <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="City Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active === 1 || formData.is_active === true}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
            }}
          >
            {loading ? <CircularProgress size={24} /> : (editingItem ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationManager;